import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import Warning from "./Warning";

function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // fetch ticker data from nseindia free api (commented becouse of CORS error )
  // useEffect(() => {
  //   const fetchIndices = async () => {
  //     try {
  //       const response = await fetch("https://www.nseindia.com/api/allIndices", {
  //         headers: {
  //           Accept: "application/json",
  //         },
  //       });

  //       const data = await response.json();
  //       console.log(data);
  //     } catch (err) {
  //       console.error(err);
  //     }
  //   };

  //   fetchIndices();
  // }, []);
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        !event.target.closest(".dropdown") &&
        !event.target.closest(".dropdown-button")
      ) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsOpen(false);
      setDropdownOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const services = [
    { name: "Cash MIS", id: "NCM" },
    { name: "MCX Pack", id: "NMCX" },
    { name: "Trade Nexus", id: "NTN" },
    { name: "Wealth Wave", id: "NWW" },
    { name: "Super Sensex", id: "SSX" },
    { name: "Stock Option MIS Pack", id: "NSOM" },
    { name: "Index Option MIS Pack", id: "NIOM" },
    { name: "Commodity Pack (Base Metal & Energy)", id: "NCMP" },
  ];

  // const tickerItems = [
  //   { name: "NIFTY 50", value: "24,320.55", change: "+0.85%", isPositive: true },
  //   { name: "BANK NIFTY", value: "52,140.10", change: "+1.12%", isPositive: true },
  //   { name: "SENSEX", value: "80,015.80", change: "+0.78%", isPositive: true },
  //   { name: "RELIANCE", value: "3,010.40", change: "-0.35%", isPositive: false },
  //   { name: "TCS", value: "4,280.00", change: "+1.45%", isPositive: true },
  // ];

  return (
    <div className="sticky top-0 z-50 font-['Plus_Jakarta_Sans',sans-serif]">
      {/* Ticker Keyframe Animation */}
      <style>{`
        @keyframes ticker {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-ticker-marquee {
          display: flex;
          width: max-content;
          animation: ticker 25s linear infinite;
        }
        .animate-ticker-marquee:hover {
          animation-play-state: paused;
        }
      `}</style>

      {/* Glassmorphism Header */}
      <header className="bg-white/90 backdrop-blur-md border-b border-slate-200 shadow-sm relative z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">

          <Link className="flex items-center" to={"/"}>
            <img
              src="/logo1.png"
              alt="Nextera Research Logo"
              width={150}
              className="cursor-pointer hover:opacity-90 transition-opacity duration-200  tracking-wider"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6 lg:gap-8 text-sm font-semibold text-slate-700">
            <Link to="/" className="hover:text-emerald-600 transition-colors">
              Home
            </Link>
            <Link to="/aboutus" className="hover:text-emerald-600 transition-colors">
              About Us
            </Link>

            {/* Services Dropdown */}
            <div className="relative group">
              <div className="flex items-center gap-1 cursor-pointer hover:text-emerald-600 transition-colors py-2">
                <Link to="/services" className="hover:text-emerald-600">
                  Services
                </Link>
                <ChevronDown size={16} className="group-hover:rotate-180 transition-transform duration-200" />
              </div>

              <div className="absolute left-0 top-full pt-2 w-64 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="bg-white border border-slate-200 shadow-xl rounded-2xl p-2 space-y-1">
                  {services.map((service) => (
                    <Link
                      key={service.id}
                      to={`/services/${service.id}`}
                      className="block px-4 py-2.5 text-xs font-semibold text-slate-700 hover:text-emerald-600 hover:bg-emerald-50 rounded-xl transition-colors"
                    >
                      {service.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            <Link to="/pricing" className="hover:text-emerald-600 transition-colors">
              Pricing
            </Link>
            <Link to="/secure-payment" className="hover:text-emerald-600 transition-colors">
              Payment
            </Link>
            <Link to="/contactus" className="hover:text-emerald-600 transition-colors">
              Contact Us
            </Link>
            <Link to="/service-api" className="hover:text-emerald-600 transition-colors">
              ATS
            </Link>
          </nav>

          {/* Login Button (Desktop) */}
          <div className="hidden md:flex items-center gap-4">

            <button
              onClick={() => window.open("https://software.nexteraresearch.in/")}
              style={{ color: "white" }}
              className="bg-emerald-600 hover:bg-emerald-700 font-bold px-6 py-2.5 rounded-xl text-sm transition-all duration-300 shadow-md shadow-emerald-600/20 hover:scale-105"
            >
              Login
            </button>

          </div>

          {/* Mobile Menu Toggle Button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 rounded-xl text-slate-700 hover:bg-slate-100 transition-colors"
              aria-label="Toggle Menu"
            >
              {isOpen ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Backdrop & Drawer Menu */}
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-40 md:hidden"
              onClick={() => setIsOpen(false)}
            />

            <motion.div
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="fixed top-0 left-0 h-full w-[80%] max-w-sm bg-white z-50 p-6 flex flex-col justify-between shadow-2xl md:hidden overflow-y-auto"
            >
              <div className="space-y-6">
                <div className="flex items-center justify-between pb-4 border-b border-slate-100">
                  <span className="text-lg font-extrabold text-slate-900">Navigation</span>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="p-1 rounded-lg text-slate-500 hover:text-slate-900"
                  >
                    <X size={24} />
                  </button>
                </div>

                <nav className="space-y-3 font-semibold text-slate-800 text-sm">
                  <Link
                    to="/"
                    className="block py-2 hover:text-emerald-600"
                    onClick={() => setIsOpen(false)}
                  >
                    Home
                  </Link>
                  <Link
                    to="/aboutus"
                    className="block py-2 hover:text-emerald-600"
                    onClick={() => setIsOpen(false)}
                  >
                    About Us
                  </Link>


                  <div>
                    <div className="flex items-center justify-between py-2 hover:text-emerald-600">
                      <Link
                        to="/services"
                        onClick={() => setIsOpen(false)}
                        className="flex-1 font-semibold"
                      >
                        Services
                      </Link>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDropdownOpen(!dropdownOpen);
                        }}
                        className="p-1 focus:outline-none"
                        aria-label="Toggle Services Menu"
                      >
                        <ChevronDown
                          size={18}
                          className={`transform transition-transform duration-200 ${dropdownOpen ? "rotate-180 text-emerald-600" : ""
                            }`}
                        />
                      </button>
                    </div>

                    {dropdownOpen && (
                      <div className="pl-4 mt-2 space-y-2 border-l-2 border-emerald-100">
                        {services.map((service) => (
                          <Link
                            key={service.id}
                            to={`/services/${service.id}`}
                            className="block py-1.5 text-xs text-slate-600 hover:text-emerald-600 font-medium"
                            onClick={() => {
                              setDropdownOpen(false);
                              setIsOpen(false);
                            }}
                          >
                            {service.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>

                  <Link
                    to="/pricing"
                    className="block py-2 hover:text-emerald-600"
                    onClick={() => setIsOpen(false)}
                  >
                    Pricing
                  </Link>
                  <Link
                    to="/secure-payment"
                    className="block py-2 hover:text-emerald-600"
                    onClick={() => setIsOpen(false)}
                  >
                    Payment
                  </Link>
                  <Link
                    to="/contactus"
                    className="block py-2 hover:text-emerald-600"
                    onClick={() => setIsOpen(false)}
                  >
                    Contact Us
                  </Link>
                  <Link
                    to="/service-api"
                    className="block py-2 hover:text-emerald-600"
                    onClick={() => setIsOpen(false)}
                  >
                    ATS (Advanced Trading Solutions)
                  </Link>
                </nav>
              </div>

              <div className="pt-6 border-t border-slate-100">
                <button
                  onClick={() => {
                    window.open("https://software.nexteraresearch.in/");
                    setIsOpen(false);
                  }}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 rounded-xl shadow-lg shadow-emerald-600/20 transition-all text-sm"
                >
                  Login
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
      <Warning/>

      {/* Live Market Ticker */}
      {/* <div className="bg-slate-900 border-y-[2px] border-[#D4AF37] text-xs py-2.5 overflow-hidden relative z-40 shadow-sm">
        <div className="animate-ticker-marquee space-x-8">
          <div className="flex items-center gap-8 whitespace-nowrap">
            {tickerItems.map((item, idx) => (
              <span key={idx} className="flex items-center gap-1.5 font-bold text-slate-200">
                <span>{item.name}</span>
                <span className={item.isPositive ? "text-emerald-400 font-semibold" : "text-rose-400 font-semibold"}>
                  {item.value}
                </span>
                <span className={`text-[10px] ${item.isPositive ? "text-emerald-400" : "text-rose-400"}`}>
                  {item.isPositive ? "▲" : "▼"} {item.change}
                </span>
              </span>
            ))}
          </div>
          <div className="flex items-center gap-8 whitespace-nowrap">
            {tickerItems.map((item, idx) => (
              <span key={`dup-${idx}`} className="flex items-center gap-1.5 font-bold text-slate-200">
                <span>{item.name}</span>
                <span className={item.isPositive ? "text-emerald-400 font-semibold" : "text-rose-400 font-semibold"}>
                  {item.value}
                </span>
                <span className={`text-[10px] ${item.isPositive ? "text-emerald-400" : "text-rose-400"}`}>
                  {item.isPositive ? "▲" : "▼"} {item.change}
                </span>
              </span>
            ))}
          </div>
        </div>
      </div> */}

    </div>
  );
}

export default Navbar;